import React, { useEffect, useRef, useState, useContext } from 'react'
import debounce from 'lodash/debounce'
import uniqBy from 'lodash/uniqBy'
import { get } from '~/utils/api'
import { WebsocketContext } from './WebsocketConn'
import { Channel } from 'phoenix'

type Tag = {
  id: number
  name: string
}

type Star = {
  colorId: number
}

export type Order = 'addedAsc' | 'addedDesc' | 'nameAsc' | 'nameDesc'

export type ClientOnList = {
  id: number
  name: string
  starred: boolean
  avatar: string
  added: Date
  tags: Tag[]
  stars?: Star[]
}

export type Filter = {
  tmCampaign?: number
  dateFrom: string | null
  dateTo: string | null
  order: Order
}

type State = {
  clients: ClientOnList[]
  loadHook: () => void
  loadMore: () => void
  setSearch: (search: string) => void
  loading: boolean
  search: string
  filter: Filter
  setFilter: React.Dispatch<React.SetStateAction<Filter>>
  order: Order
  setOrder: (order: Order) => void
}

const defaultFilter: Filter = {
  tmCampaign: undefined,
  dateFrom: null,
  dateTo: null,
  order: 'nameAsc',
  // tmCampaign: 1,
}

export const ClientListManagerContext = React.createContext<State>({
  clients: [],
  loadHook: () => {},
  loadMore: () => {},
  setSearch: () => {},
  setFilter: () => {},
  setOrder: () => {},
  loading: false,
  search: '',
  filter: defaultFilter,
  order: 'nameAsc',
})

const setClientData = (data: any) => {
  return {
    id: data.id,
    name: data.name,
    added: data.createdAt.date,
    avatar: data.avatar,
    tags: data.tags,
    guards: data.guards,
    stars: data.stars,
  }
}

type Props = { children: React.ReactNode }

const ClientListManager: React.FC<Props> = ({ children }) => {
  const wsContext = useContext(WebsocketContext)
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [order, setOrder] = useState<Order>('nameAsc')
  const [filter, setFilter] = useState(defaultFilter)
  const [initialized, setInitialized] = useState(false)
  const [items, setItems] = useState<ClientOnList[]>([])
  const [channel, setChannel] = useState<Channel | undefined>()

  const loadingRef = useRef(loading)
  loadingRef.current = loading

  const filterRef = useRef(filter)
  filterRef.current = filter

  const searchRef = useRef(search)

  const download = (params = {}, more = false) => {
    return new Promise<void>(r => {
      setLoading(true)
      loadingRef.current = true

      if (!more) {
        setItems([])
      }

      get<ClientOnList[]>(`clients/list`, {
        offset: more ? items.length : 0,
        search,
        ...filterRef.current,
        ...params,
      }).then(newItems => {
        if (more) {
          setItems(uniqBy([...items, ...newItems], e => e.id))
        } else {
          setItems(newItems)
        }

        setLoading(false)
        loadingRef.current = false
        r()
      })
    })
  }

  useEffect(() => {
    const channel = wsContext.socket.channel(
      'phone_client_list:' + window.c.campaignId
    )

    setChannel(channel)

    channel.join()

    channel.on('created', (data: any) => {
      if (searchRef.current) {
        return
      }

      const newData: any = setClientData(data)

      setItems(items => [newData, ...items])
    })

    channel.on('starred', (data: any) => {
      if (searchRef.current) {
        return
      }

      if (data.userId == window.c.userId) {
        setItems(items =>
          items.map(item =>
            item.id == data.clientId
              ? {
                  ...item,
                  stars: [{ colorId: data.colorId }],
                  starred: data.colorId != '0',
                }
              : item
          )
        )
      }
    })
  }, [])

  const loadHook = () => {
    useEffect(() => {
      if (initialized) {
        return
      }

      download().then(() => {
        setInitialized(true)
      })
    }, [])
  }

  const [searchDebounce] = useState(() =>
    debounce(search => {
      download({ search })
    }, 500)
  )

  useEffect(() => {
    if (!initialized) {
      return
    }

    searchDebounce(search)
  }, [search])

  const loadMore = () => {
    if (loadingRef.current) {
      return
    }

    download({}, true)
  }

  useEffect(() => {
    if (!initialized) {
      return
    }

    download()
  }, Object.values(filter))

  return (
    <ClientListManagerContext.Provider
      value={{
        clients: items,
        loadHook,
        loading,
        search,
        loadMore,
        setSearch,
        filter,
        setFilter,
        order,
        setOrder,
      }}
    >
      {children}
    </ClientListManagerContext.Provider>
  )
}

export default ClientListManager
